import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { withTZ } from '../../../../helpers/dateTime';
import { t } from '../../../../i18n';

const initState = {
  promoCode: null,
  editForm: null,
  isEdit: false,
  loading: false,
  formInfo: {},
};

const initialPromoCodeData = {
  amount: 0,
  amount_type: '',
  category: '',
  code: '',
  starts_at: '',
  expire_at: '',
  maximum_uses: 0,
  maximum_value: 0,
};

function validate(attr, value, state) {
  let error = '';
  const startsAtValue = state?.editForm?.starts_at?.value || null;
  const expireAtAtValue = state?.editForm?.expire_at?.value || null;
  const startsTimeValue = state?.editForm?.starts_time?.value || null;

  if (attr === 'code') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.admin.promoCodes.code_name_label') });
    }

    if (value.length >= 64) {
      error = t('dash.validation.max_length_error', { fieldName: t('dash.admin.promoCodes.code_name_label') });
    }
  }

  if (attr === 'category') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.admin.promoCodes.columns.category_title') });
    }
  }

  if (attr === 'expire_at') {
    if (startsAtValue && value && withTZ(value, 'YYYY-MM-DD').isBefore(withTZ(startsAtValue, 'YYYY-MM-DD'))) {
      return t('dash.validation.mast_be_after_error', {
        firstFieldName: t('dash.admin.promoCodes.expiry_date_label'),
        secondFieldName: t('dash.admin.promoCodes.start_date_label'),
      });
    }
  }

  if (attr === 'expire_time') {
    if (
      startsAtValue &&
      expireAtAtValue &&
      withTZ(expireAtAtValue, 'YYYY-MM-DD').isSame(withTZ(startsAtValue, 'YYYY-MM-DD')) &&
      withTZ(value, ['h:mma']).isSameOrBefore(withTZ(startsTimeValue, ['h:mma']))
    ) {
      return t('dash.validation.mast_be_after_error', {
        firstFieldName: t('dash.admin.promoCodes.expiry_time_label'),
        secondFieldName: t('dash.admin.promoCodes.start_time_label'),
      });
    }
  }

  if (attr === 'amount_type') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.admin.promoCodes.amount_type_label') });
    }
  }

  if (attr === 'maximum_uses' || attr === 'maximum_value') {
    if (value && !value.toString().match('^[0-9]*$')) {
      error = t('dash.validation.wrong_format');
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const promoCode = data;

  return {
    ...state,
    promoCode,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function onEdit(state, { payload }) {
  const { promoCode } = payload;
  const propsArray = Object.keys(promoCode);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value:
        promoCode[prop] && Object.prototype.hasOwnProperty.call(promoCode[prop], 'id')
          ? promoCode[prop].id
          : promoCode[prop],
      error: validate(prop, promoCode[prop], state),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state, { payload }) {
  const { data } = payload;
  const promoCode = data;

  return {
    ...state,
    promoCode,
    isEdit: false,
    editForm: null,
  };
}

function onDelete(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialPromoCodeData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialPromoCodeData[prop],
      error: validate(prop, initialPromoCodeData[prop], state),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    promoCode: initialPromoCodeData,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value, state),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onValidateProperty(state, { payload }) {
  const { attr } = payload;
  const { editForm } = state;

  editForm[attr] = {
    ...editForm[attr],
    error: validate(attr, editForm[attr] && editForm[attr].value, state),
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop], state),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: data,
  };
}

function onSaveErrors(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

export { validate };
export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.EDIT]: onEdit,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.VALIDATE_PROPERTY]: onValidateProperty,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.DELETE]: onDelete,
  [actions.CREATE]: onCreate,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.SAVE_ERRORS]: onSaveErrors,
});
